import { memo, useMemo } from "react";
import { Sparkles } from "lucide-react";
import type { ControllerMode, ScheduleDoc } from "@/data/types";
import { defaultParamsFor } from "@/data/modeCatalog";
import { ModePicker } from "./ModePicker";
import { ParamsEditor } from "./ParamsEditor";

/**
 * Editor for `doc.default` — the catch-all mode applied when no entry
 * covers the current time. This is the "what runs the rest of the time"
 * setting and per the task spec is operator-customisable from the cloud
 * SPA (was previously hardcoded to `gridZeroControl`).
 *
 * Recommended choice is still gridZeroControl: a misconfigured calendar
 * then falls into a productive but harmless state. We surface that hint
 * inline so the operator doesn't accidentally make OFF the default.
 */
export const DefaultEditor = memo(function DefaultEditor({
  doc,
  onChange,
}: {
  doc: ScheduleDoc;
  onChange: (next: ScheduleDoc["default"]) => void;
}) {
  const setMode = (mode: ControllerMode) => {
    // Reset params on mode change for the same reason as EntryEditor.
    onChange({ mode, params: defaultParamsFor(mode) });
  };

  const numericParams = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(doc.default.params ?? {})) {
      const n = typeof v === "number" ? v : Number(v);
      out[k] = Number.isFinite(n) ? n : 0;
    }
    return out;
  }, [doc.default.params]);

  const isOff = doc.default.mode === "OFF";

  return (
    <section className="card p-4">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-accent" />
        <div>
          <div className="text-sm font-semibold text-text">Default mode</div>
          <div className="text-[11px] text-text-muted">
            Applied when no scheduled entry covers the current time. Manual
            overrides still win over this. <strong>Recommended:</strong>{" "}
            <code className="font-mono">gridZeroControl</code> — a safe productive fallback.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[auto_1fr]">
        <ModePicker value={doc.default.mode} onChange={setMode} />
        <ParamsEditor
          mode={doc.default.mode}
          value={numericParams}
          onChange={(next) => onChange({ ...doc.default, params: next })}
        />
      </div>
      {isOff && (
        <div className="mt-2 rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-[11px] text-warn">
          OFF as a default keeps the BESS off any time no entry covers the
          calendar — typically you want this only during commissioning.
        </div>
      )}
    </section>
  );
});
